import { useState, useEffect } from 'react';
import { v4 as uuidv4 } from 'uuid';
import { useForm } from 'react-hook-form';
import {
  ButtonPseudo,
  ButtonSize,
  IconChain,
  IconExpand,
  IconLoader,
  IconTrash,
  OnSelectChange,
  ResponseError,
  responseErrorsResolver,
  Select,
  SelectItemProps,
  SelectListPosition,
} from '@ui/src';
import { CASPAliasTypes } from './types';
import { AMLBotAssets, CASPAliasFormValues } from '../../../hoc/settings/types';

import cn from './styles.module.css';

let timeout: NodeJS.Timeout;

const CaspAlias = ({
  caspAlias,
  caspAliasesList,
  setCaspAliasesList,
  index,
  casp,
  onDeleteCaspAlias,
  showAddButton,
  selectedCaspAliasId,
  deleteCaspAliasInProgress,
  creationCaspAliasErrors,
  setCreationCaspAliasErrors,
  creationCaspAliasErrorsList,
  amlBotAssets,
}: CASPAliasTypes) => {
  const [values, setValues] = useState<CASPAliasFormValues>();
  const [globalError, setGlobalError] = useState('');

  const {
    setError,
    formState: { errors },
    clearErrors,
  } = useForm();

  const onChange = (
    e: React.ChangeEvent<HTMLInputElement> | OnSelectChange,
  ) => {
    clearTimeout(timeout);

    const { name, value } = e.target;

    const nextValues = { ...values };

    if (name === `caspId-${caspAlias?.id}`) {
      nextValues.caspId = value as string;

      if (errors?.caspId?.message) {
        clearErrors(['caspId']);
      }
    } else if (name === `alias-${caspAlias?.id}`) {
      nextValues.alias = value as string;

      if (errors?.alias?.message) {
        clearErrors(['alias']);
      }
    } else if (name === `mode-${caspAlias?.id}`) {
      nextValues.mode = value as string;
    }

    setValues(nextValues as CASPAliasFormValues);

    const nextCaspAlisesList = caspAliasesList?.map((item) => {
      const result = { ...item };

      if (result.id === caspAlias?.id) {
        result.caspId = nextValues.caspId as string;
        result.alias = nextValues.alias as string;
        result.mode = nextValues.mode;
      }

      return result;
    });

    timeout = setTimeout(() => {
      setCaspAliasesList(nextCaspAlisesList as CASPAliasFormValues[]);
    }, 700);
  };

  useEffect(() => {
    if (caspAlias && caspAlias?.id?.includes('casp-alias')) {
      if (creationCaspAliasErrors) {
        responseErrorsResolver({
          errors: creationCaspAliasErrors.error,
          setError,
          setGlobalError,
        });
      } else {
        setGlobalError('');
        clearErrors(['alias', 'caspId']);
      }
    }
  }, [creationCaspAliasErrors, caspAlias]);

  useEffect(() => {
    if (caspAlias) {
      setValues(caspAlias);
    }
  }, [caspAlias]);

  return (
    <div className={cn.caspAliasWrapper}>
      {caspAlias?.id?.includes('casp-alias') && (
        <div className={cn.formGroup}>
          <Select
            name={`mode-${caspAlias?.id}`}
            value={values?.mode}
            list={[
              {
                name: 'Mode Lite',
                value: 'lite',
              },
              {
                name: 'Mode Fast',
                value: 'fast',
              },
              {
                name: 'Mode Advanced',
                value: 'advanced',
              },
            ]}
            title="Mode"
            placeholder="Select mode"
            onChange={onChange}
            fullWidth
          />
        </div>
      )}

      <div className={cn.caspAliasChain}>
        <div className={cn.caspAliasChainTop}>
          <div className={cn.caspAliasChainTopSelect}>
            <Select
              name={`caspId-${caspAlias?.id}`}
              value={values?.caspId}
              title="CASP (Internal)"
              placeholder="Select CASP"
              onChange={onChange}
              list={
                casp?.map((item) => ({ name: item.name, value: item.id })) || []
              }
              disabled={!caspAlias?.id?.includes('casp-alias')}
              error={errors?.caspId?.message as string}
              selectListPosition={SelectListPosition.TOP}
              fullWidth
            />
          </div>
          <div className={cn.caspAliasChainTopIcon}>
            <IconChain />
          </div>
          <div className={cn.caspAliasChainTopSelect}>
            <Select
              name={`alias-${caspAlias?.id}`}
              value={values?.alias}
              title="Assets (Provider String)"
              placeholder="Select Asset"
              onChange={onChange}
              list={
                values?.mode &&
                caspAlias?.id?.includes('casp-alias') &&
                amlBotAssets
                  ? amlBotAssets[values?.mode as keyof AMLBotAssets]?.map(
                      (item: string) => ({ name: item, value: item }),
                    )
                  : ([
                      {
                        name: values?.alias,
                        value: values?.alias,
                      },
                    ] as SelectItemProps[])
              }
              error={errors?.alias?.message as string}
              disabled={!values?.mode || !caspAlias?.id?.includes('casp-alias')}
              selectListPosition={SelectListPosition.TOP}
              fullWidth
            />
          </div>
          <button
            type="button"
            className={cn.deleteCaspAliasButton}
            onClick={() => {
              onDeleteCaspAlias(caspAlias?.id as string);

              if (
                caspAlias?.id?.includes('casp-alias') &&
                setCreationCaspAliasErrors
              ) {
                setGlobalError('');
                clearErrors(['caspId', 'alias']);

                const nextCreationErrorsList =
                  creationCaspAliasErrorsList?.filter(
                    (item) => item.id !== caspAlias?.id,
                  );

                setCreationCaspAliasErrors(
                  nextCreationErrorsList as {
                    id: string;
                    error: ResponseError;
                  }[],
                );
              }
            }}
          >
            {deleteCaspAliasInProgress &&
            selectedCaspAliasId === caspAlias?.id ? (
              <IconLoader width={18} height={18} />
            ) : (
              <IconTrash width={20} height={20} />
            )}
          </button>
        </div>
        {globalError && (
          <div className={cn.caspAliasGlobalError}>{globalError}</div>
        )}
        {((caspAliasesList?.length === index &&
          caspAlias?.id?.includes('casp-alias')) ||
          showAddButton) && (
          <div className={cn.caspAliasChainAddButtonWrapper}>
            <ButtonPseudo
              className={`button-white ${cn.caspAliasChainAddButton}`}
              icon={<IconExpand />}
              text="Add mappin rule"
              size={ButtonSize.SMALL}
              onClick={() => {
                const nextCaspAlisesList = ([] as CASPAliasFormValues[]).concat(
                  caspAliasesList as CASPAliasFormValues[],
                );

                nextCaspAlisesList.push({
                  id: `casp-alias-${uuidv4()}`,
                  caspId: '',
                  alias: '',
                  source: 'AML_BOT',
                  mode: 'lite',
                });

                setCaspAliasesList(nextCaspAlisesList);
              }}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default CaspAlias;
